// ---------------------------------------------------------------------------
// Financeiro — leitura de erro da API (mesmo padrão dos submódulos Vendas e
// Recrutamento). Espelha o contrato JSend de erro (fail/error) de forma
// defensiva (errors ausente / string / objeto). Some quando a infra global
// FC1/FC4 (ApiError/notifyApiError) for ligada; por ora é local ao módulo.
//
// Política (FC4): 422 com `field` → inline via `fieldErrors`; o resto → toast.
// 5xx SEMPRE exibe o `error_id` como código de suporte — é o que permite achar
// o evento no log do backend.
//
// Discrimine por `code`, NUNCA por texto da mensagem (é PT-BR e muda).
// ---------------------------------------------------------------------------

// Dicionário code → mensagem pt-BR (handoff 0079 §4). Cresce sob demanda.
export const FINANCE_ERROR_MESSAGES = {
   // ─── Contas bancárias ────────────────────────────────────────────────────
   bank_account_not_found: 'Conta bancária não encontrada.',
   bank_account_inactive:
      'A conta selecionada está inativa. Reative-a em Financeiro › Contas bancárias.',
   bank_account_status_unchanged: 'A conta já está nessa situação.',
   // ─── Emissão por conta (adendo do 0079) ──────────────────────────────────
   // A conta existe, mas não está apta a emitir. O caminho de correção é a tela
   // de contas: cadastrar a chave da ASAAS e testar a conexão.
   bank_account_without_credential:
      'A conta selecionada não tem chave de API ativa e não consegue emitir. Cadastre a credencial em Financeiro › Contas bancárias.',
   charge_without_bank_account:
      'Esta cobrança não tem conta bancária vinculada, então não é possível operá-la na ASAAS. Acione o suporte.',
   // ─── Cancelamento com motivo obrigatório ─────────────────────────────────
   cancel_reason_required:
      'Informe o motivo do cancelamento (mínimo de 8 caracteres).',
   // A cobrança ainda pode nascer na ASAAS (job pendente): cancelar agora a
   // deixaria criada lá depois de cancelada aqui. O operador precisa entender
   // que é questão de aguardar, não de permissão.
   charge_pending_at_provider:
      'Esta cobrança ainda está sendo criada na ASAAS. Aguarde a conclusão e tente cancelar novamente.',
   // ─── Validação de contato na emissão (vêm com `field`) ───────────────────
   invalid_phone: 'Telefone inválido.',
   invalid_email: 'E-mail inválido.',
   // ─── Credenciais de integração ───────────────────────────────────────────
   integration_credential_not_found: 'Credencial não encontrada.',
   credential_already_active:
      'Já existe uma credencial ativa deste provedor nesta conta. Revogue a atual antes.',
   credential_already_active_state: 'Esta credencial já está ativa.',
   credential_already_revoked: 'Esta credencial já foi revogada.',
   credential_not_active:
      'Não é possível rotacionar uma credencial revogada. Reative-a ou cadastre outra.',
   // ─── Régua de cobrança (handoff 0090) ────────────────────────────────────
   dunning_not_found: 'Caso não encontrado.',
   dunning_not_open: 'O caso não está mais em cobrança.',
   dunning_stage_not_found: 'Raia não encontrada.',
   terminal_stage_is_automatic:
      'A raia Recebido é automática — só o pagamento move o caso para lá.',
   // 403 do modelo antigo (caso inteiro escalado por drag). O backend deixou de
   // emitir no 0103; a entrada fica porque um front desatualizado em cache ainda
   // pode topar com ela durante a janela de deploy.
   external_stage_requires_permission:
      'Sem permissão para cobrança administrativa.',
   already_in_stage: 'O caso já está nessa raia.',
   charge_not_in_dunning: 'O título não pertence a este caso.',
   dunning_stage_protected:
      'Entrada, Recebido e Cobrança administrativa são raias protegidas.',
   last_attempt_stage: 'Deve existir ao menos uma raia de tentativa.',
   dunning_stage_occupied:
      'A raia tem casos ativos e não pode ser excluída.',
   auto_advance_attempt_only:
      'O avanço automático só existe em raias de tentativa.',
   // ─── Anexos da tratativa (handoff 0096) ──────────────────────────────────
   invalid_file_type:
      'Tipo de arquivo não aceito. Envie PDF, JPEG, PNG ou WEBP.',
   upload_limit:
      'Limite de anexos excedido: até 3 arquivos, de no máximo 10MB cada.',
   dunning_file_not_found: 'Anexo não encontrado.',
   // O anexo foi excluído por alguém enquanto a tela estava aberta.
   dunning_file_deleted:
      'Este anexo foi excluído e não está mais disponível para download.',
   dunning_file_already_deleted: 'Este anexo já havia sido excluído.',
   // ─── RBAC fatiado (handoff 0100) ─────────────────────────────────────────
   // Os dois 403 abaixo são REDE DE SEGURANÇA: a UI esconde o controle de quem
   // não tem a permissão, e o interceptor global do saga redireciona qualquer
   // 403 para /403 antes do catch da página exibir a mensagem. Se um deles
   // aparecer de fato, é gate faltando no front.
   assignment_change_forbidden:
      'Você não tem permissão para trocar o responsável deste caso.',
   stage_move_requires_permission:
      'Você não tem permissão para mover o caso de raia.',
   // Este é 422 e chega normalmente: o usuário escolhido não tem
   // `finance:dunning:assignable` no perfil de acesso.
   user_not_assignable:
      'Este usuário não pode ser responsável por casos de cobrança.',
   // ─── Trilha interna × externa (handoff 0103) ─────────────────────────────
   // Os dois primeiros são REDE DE SEGURANÇA: o board já não oferece a raia
   // EXTERNAL como destino nem deixa arrastar caso do escritório. Se algum
   // aparecer, é bloqueio faltando na UI.
   external_stage_not_movable:
      'A raia de cobrança administrativa não recebe casos por arraste. Use "Enviar ao escritório" e escolha os títulos.',
   external_case_not_movable:
      'Caso em cobrança administrativa não muda de raia. Devolva os títulos à régua interna primeiro.',
   // A trilha do caso não corresponde à direção pedida (escalar caso que já é
   // externo, devolver caso que é interno) — em geral, tela desatualizada.
   wrong_track_for_transfer:
      'A trilha deste caso mudou. Recarregue a tela e tente novamente.',
   // ─── Acerto com o escritório (handoff 0103 §5.4) ─────────────────────────
   check_requires_external_track:
      'Só é possível conferir títulos de casos em cobrança administrativa.',
   check_requires_settled_member:
      'Só títulos já pagos entram no acerto com o escritório.',
   check_state_unchanged: 'Este título já está nesse estado de conferência.',
   // 422 do relatório: `from`/`to` filtram a data da CONFERÊNCIA, então
   // combiná-los com "não conferidos" pediria um intervalo que não existe. A
   // tela impede a combinação; isto é rede de segurança.
   //
   // ⚠️ O handoff 0103 §5.5 descreve o 422 mas NÃO nomeia o `code`. Esta chave é
   // um palpite — se o backend usar outro nome, o toast cai no `message` dele
   // (que já é apresentável) e nada quebra. Confirmar no contrato e corrigir.
   reconciliation_range_requires_checked:
      'O período filtra a data da conferência — não combine com "Não conferidos".',
   // ─── Genéricos ───────────────────────────────────────────────────────────
   unique: 'Já existe um registro com este valor.',
   unauthorized: 'Sessão inválida. Faça login novamente.',
   forbidden: 'Você não tem permissão para esta ação.',
};

// Normaliza o erro do axios em { status, code, message, fieldErrors, errorId }.
export function readApiError(err) {
   const data = err?.response?.data;
   const status = err?.response?.status ?? 0;
   const errors = Array.isArray(data?.errors) ? data.errors : [];
   const first = errors[0] ?? null;

   const fieldErrors = {};
   errors.forEach((entry) => {
      if (entry?.field) fieldErrors[entry.field] = entry.message;
   });

   return {
      status,
      code: first?.code ?? data?.code ?? null,
      message: first?.message ?? data?.message ?? err?.message ?? null,
      fieldErrors,
      errorId: data?.error_id ?? null,
      errors,
   };
}

// Mensagem apresentável para toast: dicionário por code → mensagem do backend →
// fallback. Em 5xx, anexa o error_id como código de suporte (FC4 B.7).
// Use nos erros que NÃO são de campo (422 com field vai inline via fieldErrors).
export function financeErrorMessage(
   err,
   fallback = 'Ocorreu um erro. Tente novamente.',
) {
   const { code, message, status, errorId } = readApiError(err);

   // 413 morre no PROXY, antes do Express: o corpo é HTML do nginx, não o
   // envelope JSend — sem `code` e sem `message` do contrato. Sem este atalho o
   // operador leria "Request failed with status code 413" ao anexar arquivo
   // grande demais para o limite de corpo do servidor.
   if (status === 413) {
      return 'O envio excedeu o tamanho aceito pelo servidor. Envie arquivos menores, ou um de cada vez.';
   }

   const base = FINANCE_ERROR_MESSAGES[code] ?? message ?? fallback;

   if (status >= 500) {
      return `${base} (código de suporte: ${errorId ?? '—'})`;
   }
   return base;
}
